// src/pages/PrincipalRegistration.js (v4 – principal registration trimmed to registration-only fields)
//
// Changes vs v3:
// ✅ Removes country / class scheme / school year start controls
// ✅ Stops collecting onboarding-only school setup fields here
// ✅ Keeps roll number lookup, school website confirmation, and principal email confirmation
// ✅ Keeps prefilled green styling for backend-provided email / website until edited
//
// Intent:
// - principal-registration = registration only
// - complete-registration = display name + password
// - onboarding later = country / class scheme / school year start / school hashtag

import React, { useState, useEffect, useMemo } from "react";

import api from "../services/api";
import { API_PATHS } from "../config";
import "../styles/PrincipalRegistration.css";

const PrincipalRegistration = () => {
  const [rollNumber, setRollNumber] = useState("");
  const [schoolData, setSchoolData] = useState(null);

  const [message, setMessage] = useState("");
  const [principalEmail, setPrincipalEmail] = useState("");
  const [isRegistered, setIsRegistered] = useState(false);

  const [schoolWebsite, setSchoolWebsite] = useState("");
  const [email, setEmail] = useState("");

  // Track the “prefill” values we got from backend (so we can compare later)
  const [prefilledEmail, setPrefilledEmail] = useState("");
  const [prefilledWebsite, setPrefilledWebsite] = useState("");

  // Track whether user has edited these fields
  const [emailTouched, setEmailTouched] = useState(false);
  const [websiteTouched, setWebsiteTouched] = useState(false);

  // Scroll to top when message changes
  useEffect(() => {
    if (message) window.scrollTo({ top: 0, behavior: "smooth" });
  }, [message]);

  const fetchSchoolData = async (cleanedRollNumber) => {
    if (!cleanedRollNumber) {
      setSchoolData(null);
      setPrincipalEmail("");
      setEmail("");
      setSchoolWebsite("");
      setPrefilledEmail("");
      setPrefilledWebsite("");
      setEmailTouched(false);
      setWebsiteTouched(false);
      setMessage("");
      return;
    }

    try {
      const { data } = await api.get(API_PATHS.GET_SCHOOL_DATA(cleanedRollNumber));
      setSchoolData(data);

      const nextEmail = data.principal_email || "";
      const nextWebsite = data.school_website || "";

      setPrincipalEmail(nextEmail);

      // Set values from backend
      setEmail(nextEmail);
      setSchoolWebsite(nextWebsite);

      // Remember the prefilled baseline
      setPrefilledEmail(nextEmail);
      setPrefilledWebsite(nextWebsite);

      // Reset touched state (because we just refreshed from backend)
      setEmailTouched(false);
      setWebsiteTouched(false);

      setMessage("");
    } catch (error) {
      setSchoolData(null);
      setPrincipalEmail("");
      setEmail("");
      setSchoolWebsite("");
      setPrefilledEmail("");
      setPrefilledWebsite("");
      setEmailTouched(false);
      setWebsiteTouched(false);
      setMessage("School not found. Please check the Roll Number.");
    }
  };

  const handleRegister = async () => {
    window.scrollTo({ top: 0, behavior: "smooth" });

    if (!/\S+@\S+\.\S+/.test(email)) {
      setMessage("Please enter a valid email address.");
      return;
    }

    if (!schoolWebsite.trim()) {
      setMessage("Please enter your school's website URL.");
      return;
    }

    const payload = {
      email: email.trim(),
      role: "PRINCIPAL",
      roll_number: rollNumber.trim(),
      school_website: schoolWebsite.trim(),
    };

    try {
      const { data } = await api.post(API_PATHS.PRINCIPAL_REGISTRATION, payload);
      setMessage(data.message || "Registration successful! Check your email.");
      setIsRegistered(true);
    } catch (error) {
      setMessage(error?.response?.data?.detail || "Registration failed. Please try again.");
    }
  };

  // “Prefilled style” means: it matches backend prefill AND user hasn’t edited it yet.
  const emailIsPrefilled = useMemo(() => {
    if (!prefilledEmail) return false;
    if (emailTouched) return false;
    return email === prefilledEmail;
  }, [email, prefilledEmail, emailTouched]);

  const websiteIsPrefilled = useMemo(() => {
    if (!prefilledWebsite) return false;
    if (websiteTouched) return false;
    return schoolWebsite === prefilledWebsite;
  }, [schoolWebsite, prefilledWebsite, websiteTouched]);

  return (
    <div className="principal-registration page">
      <div className="registration-card">
        <header className="registration-header">
          <h1 className="registration-title">Principal Registration</h1>
          <p className="registration-subtitle">
            Start your free 7-day trial — no card required. We’ll match your school by Roll Number
            and send your login link by email.
          </p>
        </header>

        {message && (
          <p
            className={`message ${
              message.includes("failed") || message.includes("not found") ? "error" : "success"
            }`}
            aria-live="polite"
          >
            {message}
          </p>
        )}

        {/* Step 1: Find school */}
        <section className="registration-section">
          <h2 className="section-title">1. Find your school</h2>
          <div className="form-group">
            <input
              id="roll-number"
              type="text"
              className="input-field"
              placeholder="Enter your school’s Roll Number"
              value={rollNumber}
              onChange={(e) => {
                const cleaned = e.target.value.trim();
                setRollNumber(cleaned);
                if (cleaned.length >= 5) fetchSchoolData(cleaned);
              }}
              onBlur={(e) => setRollNumber(e.target.value.trim())}
            />
          </div>

          {schoolData && (
            <div className="school-details">
              <p>
                <strong>School Name:</strong> {schoolData.official_name}
              </p>
              <p>
                <strong>Address:</strong> {schoolData.address_line_1}, {schoolData.county_description}
              </p>
              <p>
                <strong>Eircode:</strong> {schoolData.eircode}
              </p>
              <p>
                <strong>Phone:</strong> {schoolData.phone_no}
              </p>
              <p>
                <strong>Principal:</strong> {schoolData.principal_name}
              </p>
              <p>
                <strong>Principal Email:</strong> {principalEmail}
              </p>
              <p>
                <strong>Enrolment:</strong> {schoolData.enrolment_quantity}
              </p>
              {schoolData.school_website && (
                <p>
                  <strong>Website on file:</strong>{" "}
                  <a href={schoolData.school_website} target="_blank" rel="noopener noreferrer">
                    {schoolData.school_website}
                  </a>
                </p>
              )}
            </div>
          )}

          <div className="form-group">
            <span className="tooltip">
              Detected School Website (update it please if it&apos;s incorrect).
            </span>
            <input
              id="school-website"
              type="url"
              className={`input-field ${websiteIsPrefilled ? "is-prefilled" : ""}`}
              placeholder="https://www.yourschool.ie"
              value={schoolWebsite}
              onChange={(e) => {
                if (!websiteTouched) setWebsiteTouched(true);
                setSchoolWebsite(e.target.value);
              }}
              required
            />
          </div>
        </section>

        {/* Step 2: Email */}
        <section className="registration-section">
          <h2 className="section-title">2. Confirm your email</h2>
          <div className="form-group">
            <label htmlFor="email">Email</label>
            <input
              id="email"
              type="email"
              className={`input-field ${emailIsPrefilled ? "is-prefilled" : ""}`}
              placeholder="Your principal email"
              value={email}
              onChange={(e) => {
                if (!emailTouched) setEmailTouched(true);
                setEmail(e.target.value);
              }}
              required
            />
            <span className="tooltip">Change your email address to the most convenient one.</span>
          </div>
        </section>

        <button
          className="register-button"
          onClick={handleRegister}
          disabled={!rollNumber || !email || !schoolData || !schoolWebsite.trim() || isRegistered}
        >
          {isRegistered ? "Registered ✅" : "Register"}
        </button>
      </div>
    </div>
  );
};

export default PrincipalRegistration;